import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import { uploadImage } from '../../utils/uploadFile';
import Notification from '../../components/Notification';
import ConfirmDialog from '../../components/ConfirmDialog';
import APP_CONFIG from '../../config/appConfig';
import { ProtectedRoute } from '../../utils/authGuard';

const ManageBlogs = () => {
  const { data: blogs, loading, addItem, updateItem, deleteItem } = useAPI('blogs');
  
  // Ensure blogs is always an array
  const safeBlogs = Array.isArray(blogs) ? blogs : [];
  
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    image: null
  });
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    blog: null,
    title: '',
    message: ''
  });

  // Character limits
  const limits = {
    title: 100,
    excerpt: 200,
    content: 2000
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = limits[name];
    
    if (limit && value.length > limit) {
      return; // Don't update if over limit
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    setFormData({
      ...formData,
      image: e.target.files[0]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let imageUrl = '';
      if (formData.image) {
        imageUrl = await uploadImage(formData.image, 'blogs');
      } else if (editingBlog && editingBlog.image) {
        imageUrl = editingBlog.image;
      }

      const blogData = {
        ...formData,
        image: imageUrl,
        createdAt: editingBlog ? editingBlog.createdAt : new Date()
      };

      if (editingBlog) {
        const blogId = editingBlog.id || editingBlog._id;
        await updateItem(blogId, blogData);
      } else {
        await addItem(blogData);
      }

      setNotification({
        message: editingBlog ? 'Blog updated successfully!' : 'Blog added successfully!',
        type: 'success'
      });
      
      setFormData({ title: '', excerpt: '', content: '', category: '', image: null });
      setShowForm(false);
      setEditingBlog(null);
    } catch (error) {
      setNotification({
        message: 'Failed to save blog. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      image: null
    });
    setIsSubmitting(false);
    setShowForm(true);
  };

  const handleDelete = async (blog) => {
    setConfirmDialog({
      isOpen: true,
      blog: blog,
      title: 'Delete Blog Post',
      message: `Are you sure you want to delete "${blog.title}"? This action cannot be undone.`
    });
  };

  const confirmDelete = async () => {
    try {
      const blogId = confirmDialog.blog.id || confirmDialog.blog._id;
      
      await deleteItem(blogId);
      
      setNotification({
        message: 'Blog deleted successfully!',
        type: 'success'
      });
    } catch (error) {
      setNotification({
        message: 'Failed to delete blog. Please try again.',
        type: 'error'
      });
    } finally {
      setConfirmDialog({
        isOpen: false,
        blog: null,
        title: '',
        message: ''
      });
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({
      isOpen: false,
      blog: null,
      title: '',
      message: ''
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    const baseServerUrl = APP_CONFIG.getBaseServerUrl();
    return `${baseServerUrl}${image}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-black">Manage Blogs</h1>
              <p className="text-black mt-1 sm:mt-2 text-sm sm:text-base">Create and manage your blog posts</p>
            </div>
            <button
              onClick={() => {
                setShowForm(true);
                setIsSubmitting(false);
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Add New Blog
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-black mb-3 sm:mb-4">
                {editingBlog ? 'Edit Blog Post' : 'Add New Blog Post'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Title
                      <span className="text-gray-600 text-xs ml-2">
                        ({formData.title.length}/{limits.title})
                      </span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      maxLength={limits.title}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-black ${
                        formData.title.length > limits.title * 0.9 
                          ? 'border-yellow-400 bg-yellow-50' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter blog title..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">Category</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                      placeholder="Enter category..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Excerpt
                    <span className="text-gray-600 text-xs ml-2">
                      ({formData.excerpt.length}/{limits.excerpt})
                    </span>
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    required
                    maxLength={limits.excerpt}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-black ${
                      formData.excerpt.length > limits.excerpt * 0.9 
                        ? 'border-yellow-400 bg-yellow-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Brief description of your blog post..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Content
                    <span className="text-gray-600 text-xs ml-2">
                      ({formData.content.length}/{limits.content})
                    </span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    maxLength={limits.content}
                    rows={8}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-black ${
                      formData.content.length > limits.content * 0.9 
                        ? 'border-yellow-400 bg-yellow-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Write your blog content here..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Featured Image</label>
                  <div className="space-y-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-black"
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-black mb-2">Preview:</p>
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                    {editingBlog && editingBlog.image && !formData.image && (
                      <div className="mt-2">
                        <p className="text-sm text-black mb-2">Current Image:</p>
                        <img
                          src={getImageUrl(editingBlog.image)}
                          alt="Current"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {isSubmitting 
                      ? (editingBlog ? 'Updating...' : 'Creating...') 
                      : `${editingBlog ? 'Update' : 'Create'} Blog`}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingBlog(null);
                      setFormData({ title: '', excerpt: '', content: '', category: '', image: null });
                      setIsSubmitting(false);
                    }}
                    className="bg-gray-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Blogs List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-black">All Blog Posts</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {safeBlogs.map((blog) => (
                <div key={blog.id} className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    {/* Blog Image */}
                    <div className="flex-shrink-0">
                      {getImageUrl(blog.image) ? (
                        <img
                          src={getImageUrl(blog.image)}
                          alt={blog.title}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-lg flex items-center justify-center ${getImageUrl(blog.image) ? 'hidden' : 'flex'}`}
                      >
                        <span className="text-gray-400 text-xs">No Image</span>
                      </div>
                    </div>
                    
                    {/* Blog Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-black mb-1">{blog.title}</h4>
                      <p className="text-black text-xs sm:text-sm mb-2 line-clamp-2">{blog.excerpt}</p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                          {blog.category}
                        </span>
                        <span className="text-black">
                          {new Date(blog.createdAt?.toDate?.() || blog.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-gray-600">
                          {blog.content?.length || 0} characters
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2 self-start sm:self-center">
                      <button
                        onClick={() => handleEdit(blog)}
                        className="text-blue-600 hover:text-blue-700 px-2 sm:px-3 py-1 rounded hover:bg-blue-50 transition-colors text-xs sm:text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(blog)}
                        className="text-red-600 hover:text-red-700 px-2 sm:px-3 py-1 rounded hover:bg-red-50 transition-colors text-xs sm:text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {blogs.length === 0 && (
                <div className="p-6 text-center text-black">
                  No blog posts yet. Create your first blog post!
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 sm:mt-8">
            <Link
                to="/admin/dashboard"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
  );
};

const ProtectedManageBlogs = () => (
  <ProtectedRoute>
    <ManageBlogs />
  </ProtectedRoute>
);

export default ProtectedManageBlogs;
